import { useTranslation } from "react-i18next";
import { User, Building2, Info, UserCog } from "lucide-react";
import { SectionCard } from "@/components/common";
import { HoverPopover, brandFillClass } from "@/components/ui";
import { ProfileType } from "@/types";
import { cn } from "@/lib/utils";

interface ProfileTypeSelectorProps {
  anchorId: string;
  regionCode: string;
  value: ProfileType;
  onChange: (type: ProfileType) => void;
}

export function ProfileTypeSelector({
  anchorId,
  regionCode,
  value,
  onChange,
}: ProfileTypeSelectorProps) {
  const { t } = useTranslation("settings");

  return (
    <SectionCard
      id={anchorId}
      className="scroll-mt-24"
      title={t("forms.profileType.cardTitle")}
      icon={UserCog}
      action={
        <HoverPopover
          placement="below"
          content={t("forms.region.hint")}
        >
          {/* Focusable so keyboard and touch users can open the hint too. */}
          <span
            tabIndex={0}
            className="text-surface-500 inline-flex cursor-help items-center gap-1.5 rounded-md text-xs font-medium"
          >
            <Info
              className="h-3.5 w-3.5"
              aria-hidden
            />
            {t("forms.region.cardTitle")}
            <span className="bg-primary-50 text-primary-700 ring-primary-600/15 inline-flex h-6 items-center rounded-md px-2 font-mono text-xs font-semibold tracking-wide ring-1 ring-inset">
              {regionCode.trim() || "—"}
            </span>
          </span>
        </HoverPopover>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => onChange(ProfileType.PERSON)}
          className={cn(
            "flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200",
            value === ProfileType.PERSON
              ? "border-primary-500 bg-primary-50 ring-primary-500 ring-1"
              : "border-surface-200 bg-surface-0 hover:border-surface-300 hover:shadow-elevated hover:-translate-y-px",
          )}
        >
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
              value === ProfileType.PERSON
                ? cn("shadow-xs", brandFillClass("primary"))
                : "bg-surface-50 text-surface-500 ring-surface-200 ring-1 ring-inset",
            )}
          >
            <User className="h-5 w-5" />
          </div>
          <div className="text-left">
            <p className="text-surface-900 text-sm font-semibold">
              {t("forms.profileType.individualTitle")}
            </p>
            <p className="text-surface-500 text-[13px]">
              {t("forms.profileType.individualDesc")}
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => onChange(ProfileType.COMPANY)}
          className={cn(
            "flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200",
            value === ProfileType.COMPANY
              ? "border-primary-500 bg-primary-50 ring-primary-500 ring-1"
              : "border-surface-200 bg-surface-0 hover:border-surface-300 hover:shadow-elevated hover:-translate-y-px",
          )}
        >
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
              value === ProfileType.COMPANY
                ? cn("shadow-xs", brandFillClass("primary"))
                : "bg-surface-50 text-surface-500 ring-surface-200 ring-1 ring-inset",
            )}
          >
            <Building2 className="h-5 w-5" />
          </div>
          <div className="text-left">
            <p className="text-surface-900 text-sm font-semibold">
              {t("forms.profileType.businessTitle")}
            </p>
            <p className="text-surface-500 text-[13px]">
              {t("forms.profileType.businessDesc")}
            </p>
          </div>
        </button>
      </div>
    </SectionCard>
  );
}
